import React, { useState, useEffect } from 'react'
import useAuthContext from '../../hooks/useAuthContext'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Drawer, Dropdown, notification } from 'antd'
import { CheckCircleOutlined, DashboardOutlined } from '@ant-design/icons'
import Logo from '../../assets/img/LogoÑuñoa.png'
import { FiShoppingCart, FiMenu, FiX, FiChevronRight, FiGrid, FiUser, FiHeart, FiSettings, FiLogOut, FiInfo, FiBookOpen, FiAward, FiCreditCard, FiPhone, FiExternalLink } from 'react-icons/fi'
import { signOut } from 'aws-amplify/auth'
import { useCart } from '../../providers/Cart'
import { entraAlPanel } from '../../config/rbac'
import { getMenuItemsByUser } from '../../config/menuAdminConfig'
import { MdQrCode2 } from 'react-icons/md'
import QRAccesoModal from '../Acceso/QRAccesoModal'
import useQRAcceso from '../../hooks/useQRAcceso'

const Navbar = () => {
    const { user, isSignedIn, setIsSignedIn } = useAuthContext()
    const navigate = useNavigate()
    const location = useLocation()
    const [api, contextHolder] = notification.useNotification()
    const { getCartCount } = useCart()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const { generar: generarQR, modalProps: qrModalProps } = useQRAcceso()

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 8)
        window.addEventListener('scroll', handler, { passive: true })
        return () => window.removeEventListener('scroll', handler)
    }, [])

    // Cerrar drawer al cambiar ruta
    useEffect(() => { setDrawerOpen(false) }, [location.pathname])

    const handleLogout = async () => {
        try {
            setDrawerOpen(false)
            api.info({ message: 'Cerrando sesión…', duration: 0, key: 'lo' })
            await signOut()
            setIsSignedIn(false)
            navigate('/')
            api.destroy('lo')
            api.success({
                message: '¡Hasta luego!',
                icon: <CheckCircleOutlined style={{ color: '#0047bb' }} />,
                style: { borderLeft: '4px solid #0047bb', borderRadius: 12 }
            })
        } catch {
            api.destroy('lo')
            api.error({ message: 'Error al cerrar sesión' })
        }
    }

    // A dónde lleva el botón «Panel Admin»: la primera pantalla que esta persona
    // puede abrir de verdad. Antes esto era una cadena de banderas de rol y mandaba a
    // /admin/talleres/lista por defecto, así que cualquier rol nuevo aterrizaba en una
    // pantalla que quizá no tiene permitida.
    const getDashRoute = () => {
        const primera = getMenuItemsByUser(user)[0]
        const hijo = primera?.children?.[0]
        return '/admin/' + (hijo?.key || primera?.key || 'dashboard')
    }

    // ¿Ve el acceso al panel? Se pregunta por permisos, no por rol: así el rol
    // `funcionario` —que existe solo para el torniquete— no lo ve, y un rol creado
    // desde el panel sí.
    const isAdmin = entraAlPanel(user)
    const cartCount = getCartCount()

    // Las secciones viven en el home: si ya estamos ahí se baja con scroll; si no, se
    // navega con el hash y es el home quien hace el scroll al montar.
    const irASeccion = (id) => {
        setDrawerOpen(false)
        if (location.pathname === '/') {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
        } else {
            navigate(`/#${id}`)
        }
    }

    const NAV_LINKS = [
        { label: 'Talleres', seccion: 'talleres-section', icon: <FiGrid size={18} /> },
        { label: 'Tarjeta Vecino', seccion: 'tarjeta-vecino', icon: <FiCreditCard size={18} /> },
        { label: 'Contacto', seccion: 'contacto', icon: <FiPhone size={18} /> },
        { label: 'Nosotros', href: 'https://nunoadeportes.cl/', icon: <FiInfo size={18} /> },
    ]
    const CUENTA_LINKS = [
        { to: '/perfil', label: 'Mi perfil', detalle: 'Tus datos personales', icon: <FiUser size={18} /> },
        { to: '/mis-talleres', label: 'Mis talleres', detalle: 'Inscripciones activas y QR de acceso', icon: <FiBookOpen size={18} /> },
        { to: '/mis-matriculas', label: 'Mis matrículas', detalle: 'Estado y vencimiento', icon: <FiAward size={18} /> },
        { to: '/familiares', label: 'Mis cargas', detalle: 'Familiares que puedes inscribir', icon: <FiHeart size={18} /> },
    ]
    const iniciales = (user?.name || '')
        .split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || <FiUser size={16} />

    const linkBase = 'px-3.5 py-2 text-sm font-semibold rounded-full transition-all'
    const linkIdle = 'text-gray-600 hover:text-primary hover:bg-blue-50'

    return (
        <>
            {contextHolder}
            <QRAccesoModal {...qrModalProps} />

            {/* ── NAVBAR ── */}
            <header className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? 'bg-white/95 backdrop-blur-xl shadow-[0_2px_24px_rgba(0,0,0,0.08)]'
                    : 'bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)]'
            }`}>
                <div className="max-w-screen-xl mx-auto px-4 sm:px-6 h-16 flex items-center gap-4">

                    {/* Logo + nombre */}
                    <Link to="/" className="flex-shrink-0 flex items-center gap-3">
                        <img src={Logo} alt="" className="h-10 w-auto" />
                        <span className="hidden sm:flex flex-col leading-tight">
                            <span className="text-[15px] font-extrabold text-gray-900">Ñuñoa Deportes</span>
                            <span className="text-xs font-medium text-gray-500">Talleres municipales</span>
                        </span>
                    </Link>

                    {/* Nav links — desktop */}
                    <nav className="hidden lg:flex items-center gap-1 ml-6" aria-label="Principal">
                        {NAV_LINKS.map(link => (
                            link.href
                                ? <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer"
                                    className={`${linkBase} ${linkIdle} inline-flex items-center gap-1.5`}>
                                    {link.label}
                                    <FiExternalLink size={13} aria-hidden className="opacity-60" />
                                    <span className="sr-only">(abre en otra pestaña)</span>
                                </a>
                                : <button key={link.label} type="button" onClick={() => irASeccion(link.seccion)}
                                    className={`${linkBase} ${linkIdle}`}>
                                    {link.label}
                                </button>
                        ))}
                        {isSignedIn && (
                            <Link to="/mis-talleres"
                                className={`${linkBase} ${location.pathname === '/mis-talleres' ? 'text-primary bg-blue-50' : linkIdle}`}>
                                Mis talleres
                            </Link>
                        )}
                    </nav>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Actions */}
                    <div className="flex items-center gap-1 sm:gap-2">

                        {/* Mi QR: a un toque desde cualquier página. Si todavía no
                            corresponde (vecino fuera de su bloque), el modal lo explica. */}
                        {isSignedIn && (
                            <button type="button" onClick={generarQR} aria-label="Mi QR de acceso al recinto"
                                className="inline-flex items-center gap-1.5 p-2.5 xl:px-3.5 xl:py-2 rounded-full text-gray-600 hover:bg-[#fffbea] hover:text-[#00266b] xl:border xl:border-gray-200 xl:hover:border-secondary transition-colors">
                                <MdQrCode2 size={20} />
                                <span className="hidden xl:inline text-sm font-semibold">Mi QR</span>
                            </button>
                        )}

                        {/* Carrito */}
                        <Link to="/carrito"
                            aria-label={cartCount > 0 ? `Carrito, ${cartCount} producto${cartCount !== 1 ? 's' : ''}` : 'Carrito'}
                            className="relative p-2.5 rounded-full hover:bg-gray-100 transition-colors text-gray-600 hover:text-primary">
                            <FiShoppingCart size={20} />
                            {cartCount > 0 && (
                                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                                    {cartCount}
                                </span>
                            )}
                        </Link>

                        {/* Authenticated — desktop */}
                        {isSignedIn ? (
                            <div className="hidden lg:flex items-center gap-2">
                                {isAdmin && (
                                    <Link to={getDashRoute()}
                                        className="hidden xl:inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-primary border border-blue-200 rounded-full hover:bg-blue-50 transition-all">
                                        <DashboardOutlined /> Panel
                                    </Link>
                                )}
                                <Dropdown
                                    placement="bottomRight"
                                    trigger={['click']}
                                    open={menuOpen}
                                    onOpenChange={setMenuOpen}
                                    popupRender={() => (
                                        <div className="w-[320px] bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-[0_24px_48px_-12px_rgba(0,38,107,.25)]">
                                            {/* Cabecera: quién está conectado */}
                                            <div className="relative px-5 pt-5 pb-4 bg-[#00266b] overflow-hidden">
                                                <span aria-hidden className="absolute -right-6 -top-8 w-24 h-24 rounded-full bg-secondary/90" />
                                                <span aria-hidden className="absolute right-12 top-10 w-6 h-6 rounded-full bg-secondary/40" />
                                                <div className="relative flex items-center gap-3">
                                                    <span className="w-12 h-12 flex-shrink-0 rounded-full bg-white text-primary font-extrabold text-base flex items-center justify-center ring-4 ring-white/15">
                                                        {iniciales}
                                                    </span>
                                                    <div className="min-w-0 pr-10">
                                                        <p className="font-bold text-white text-[15px] truncate m-0">{user?.name}</p>
                                                        <p className="text-xs text-blue-100 truncate m-0">{user?.email}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Accesos de la cuenta */}
                                            <nav className="p-2" aria-label="Mi cuenta">
                                                {CUENTA_LINKS.map(item => {
                                                    const activo = location.pathname === item.to
                                                    return (
                                                        <Link key={item.to} to={item.to} onClick={() => setMenuOpen(false)}
                                                            aria-current={activo ? 'page' : undefined}
                                                            className={`group flex items-center gap-3 px-3 py-2.5 rounded-2xl transition-colors
                                                                ${activo ? 'bg-[#fffbea]' : 'hover:bg-gray-50'}`}>
                                                            <span className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors
                                                                ${activo ? 'bg-secondary text-[#00266b]' : 'bg-blue-50 text-primary group-hover:bg-secondary group-hover:text-[#00266b]'}`}>
                                                                {item.icon}
                                                            </span>
                                                            <span className="min-w-0 flex-1">
                                                                <span className="block text-sm font-bold text-gray-900">{item.label}</span>
                                                                <span className="block text-xs text-gray-500 truncate">{item.detalle}</span>
                                                            </span>
                                                            <FiChevronRight size={15} className="text-gray-300 group-hover:text-gray-500 transition-colors" />
                                                        </Link>
                                                    )
                                                })}

                                                {isAdmin && (
                                                    <Link to={getDashRoute()} onClick={() => setMenuOpen(false)}
                                                        className="group mt-1 flex items-center gap-3 px-3 py-2.5 rounded-2xl bg-primary !text-white hover:bg-[#00266b] transition-colors">
                                                        {/* Colores fijados en cada hijo: el a:hover global de antd
                                                            pintaba el texto celeste sobre el fondo azul. */}
                                                        <span className="w-10 h-10 flex-shrink-0 rounded-xl bg-white/15 text-white flex items-center justify-center group-hover:bg-secondary group-hover:text-[#00266b] transition-colors">
                                                            <FiSettings size={18} />
                                                        </span>
                                                        <span className="flex-1">
                                                            <span className="block text-sm font-bold text-white">Panel de control</span>
                                                            <span className="block text-xs text-blue-100">Administración del sistema</span>
                                                        </span>
                                                        <FiChevronRight size={15} className="text-white/60 group-hover:text-white transition-colors" />
                                                    </Link>
                                                )}
                                            </nav>

                                            <div className="px-2 pb-2 space-y-1">
                                                <button type="button" onClick={() => { setMenuOpen(false); generarQR() }}
                                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl bg-secondary text-sm font-bold text-[#00266b] hover:brightness-105 transition-all">
                                                    <MdQrCode2 size={17} /> Mi QR de acceso
                                                </button>
                                                <button type="button" onClick={() => { setMenuOpen(false); handleLogout() }}
                                                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100">
                                                    <FiLogOut size={15} /> Cerrar sesión
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                >
                                    <button type="button" aria-label="Menú de mi cuenta"
                                        className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition-colors
                                            ${menuOpen ? 'border-secondary bg-[#fffbea]' : 'border-transparent hover:bg-gray-100'}`}>
                                        <span className="w-8 h-8 rounded-full bg-primary text-white text-xs font-extrabold flex items-center justify-center">
                                            {iniciales}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-700 hidden xl:block max-w-[120px] truncate">
                                            {user?.name?.split(' ')[0]}
                                        </span>
                                        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden
                                            className={`text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : ''}`}>
                                            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                </Dropdown>
                            </div>
                        ) : (
                            <div className="hidden lg:flex items-center gap-2">
                                <Link to="/login"
                                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-primary rounded-full hover:bg-gray-100 transition-all">
                                    Ingresar
                                </Link>
                                <Link to="/registro"
                                    className="px-5 py-2 text-sm font-bold text-white bg-primary hover:brightness-110 rounded-full transition-all shadow-sm hover:shadow-md">
                                    Registrarse
                                </Link>
                            </div>
                        )}

                        {/* Hamburger — mobile */}
                        <button onClick={() => setDrawerOpen(true)}
                            className="lg:hidden p-2.5 rounded-full hover:bg-gray-100 transition-colors text-gray-600"
                            aria-label="Menú">
                            <FiMenu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            {/* ── DRAWER MÓVIL ── */}
            <Drawer
                placement="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={288}
                styles={{ header: { display: 'none' }, body: { padding: 0 } }}
            >
                <div className="flex flex-col h-full bg-white">
                    {/* Header drawer */}
                    <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
                        <img src={Logo} alt="Logo" className="h-8 w-auto" />
                        <button onClick={() => setDrawerOpen(false)}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors text-gray-500">
                            <FiX size={18} />
                        </button>
                    </div>

                    {/* User info */}
                    {isSignedIn && user && (
                        <div className="mx-4 mt-4 p-4 rounded-2xl" style={{ background: 'linear-gradient(135deg,#e6eefb,#eff6ff)' }}>
                            <div className="flex items-center gap-3">
                                <span className="w-11 h-11 flex-shrink-0 rounded-full bg-primary text-white font-extrabold text-sm flex items-center justify-center">
                                    {iniciales}
                                </span>
                                <div className="min-w-0">
                                    <p className="font-bold text-gray-900 text-sm truncate">{user.name}</p>
                                    <p className="text-xs text-blue-600 truncate">{user.email}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Nav links */}
                    <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-2 mb-2">Explorar</p>
                        {NAV_LINKS.map(item => (
                            <button key={item.label}
                                onClick={() => item.href ? window.open(item.href, '_blank', 'noopener') : irASeccion(item.seccion)}
                                className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors group">
                                <span className="text-gray-400 group-hover:text-primary transition-colors">{item.icon}</span>
                                <span className="flex-1 font-semibold text-[14px] text-gray-700 text-left">{item.label}</span>
                                {item.href
                                    ? <FiExternalLink size={14} className="text-gray-300" />
                                    : <FiChevronRight size={14} className="text-gray-300" />}
                            </button>
                        ))}

                        {isSignedIn && (
                            <>
                                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-2 mb-2 mt-4">Mi cuenta</p>
                                <button type="button" onClick={() => { setDrawerOpen(false); generarQR() }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 mb-1 rounded-xl bg-secondary text-[#00266b] font-bold text-[14px] hover:brightness-105 transition-all">
                                    <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-white/60 flex items-center justify-center">
                                        <MdQrCode2 size={20} />
                                    </span>
                                    Mi QR de acceso
                                </button>
                                {[
                                    ...CUENTA_LINKS,
                                    ...(isAdmin ? [{ icon: <FiSettings size={18} />, label: 'Panel de control', to: getDashRoute() }] : []),
                                ].map(item => {
                                    const activo = location.pathname === item.to
                                    return (
                                        <button key={item.label}
                                            onClick={() => navigate(item.to)}
                                            aria-current={activo ? 'page' : undefined}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors group ${activo ? 'bg-[#fffbea]' : 'hover:bg-gray-50'}`}>
                                            <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors
                                                ${activo ? 'bg-secondary text-[#00266b]' : 'bg-blue-50 text-primary group-hover:bg-secondary group-hover:text-[#00266b]'}`}>
                                                {item.icon}
                                            </span>
                                            <span className="flex-1 font-semibold text-[14px] text-gray-700 text-left">{item.label}</span>
                                            <FiChevronRight size={14} className="text-gray-300" />
                                        </button>
                                    )
                                })}
                            </>
                        )}
                    </div>

                    {/* Footer drawer */}
                    <div className="px-4 pb-8 pt-3 border-t border-gray-100">
                        {isSignedIn ? (
                            <button onClick={handleLogout}
                                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-red-50 hover:bg-red-100 text-red-600 font-bold text-sm transition-colors">
                                <FiLogOut size={16} /> Cerrar sesión
                            </button>
                        ) : (
                            <div className="space-y-2">
                                <Link to="/registro" onClick={() => setDrawerOpen(false)}
                                    className="block w-full py-3 rounded-2xl bg-primary hover:brightness-110 text-white font-bold text-sm text-center transition-colors shadow-sm">
                                    Crear cuenta gratis
                                </Link>
                                <Link to="/login" onClick={() => setDrawerOpen(false)}
                                    className="block w-full py-3 rounded-2xl border border-gray-200 text-gray-700 font-semibold text-sm text-center hover:bg-gray-50 transition-colors">
                                    Ingresar
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </Drawer>
        </>
    )
}

export default Navbar
